import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BarChart3, RefreshCw, AlertTriangle, CheckCircle, XCircle, FileText,
  Globe, Cpu, Clock, Zap, Languages, Key, FileEdit, Trash2
} from 'lucide-react';
import { useAdminT } from '@/lib/adminTranslations';

const API_BASE = '';
const LANGS = ['en', 'tr', 'es', 'pt', 'de', 'fr', 'it', 'nl', 'ru', 'uk', 'ar', 'id'];

export default function AdminSEODashboard() {
  const t = useAdminT();
  const token = localStorage.getItem('admin_token');
  const [overview, setOverview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [batchLang, setBatchLang] = useState('en');
  const [pagesLang, setPagesLang] = useState('en');
  const [pagesType, setPagesType] = useState('');

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [enhanced, setEnhanced] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ language: pagesLang, limit: '30' });
      if (pagesType) params.append('page_type', pagesType);
      const [ovRes, logRes, pgRes, enhRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/seo/overview`, { headers }),
        fetch(`${API_BASE}/api/admin/seo/logs?limit=25`, { headers }),
        fetch(`${API_BASE}/api/admin/seo/pages?${params}`, { headers }),
        fetch(`${API_BASE}/api/admin/seo/enhanced-overview`, { headers }).catch(() => null),
      ]);
      setOverview(await ovRes.json());
      const lg = await logRes.json();
      setLogs(lg.logs || []);
      const pg = await pgRes.json();
      setPages(pg.pages || []);
      if (enhRes && enhRes.ok) setEnhanced(await enhRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, pagesLang, pagesType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleBatchRegenerate = async (pageType) => {
    setBatchStatus(`${pageType} (${batchLang})...`);
    try {
      const res = await fetch(`${API_BASE}/api/admin/seo/batch-regenerate?page_type=${pageType}&language=${batchLang}&limit=10`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      setBatchStatus(data.status === 'started' ? `${pageType} started` : `${pageType} done`);
      setTimeout(() => { setBatchStatus(null); fetchData(); }, 3000);
    } catch (e) { setBatchStatus('Error'); }
  };

  const handleBulkGenerate = async (endpoint) => {
    setBatchStatus(`${endpoint} (${batchLang}) starting...`);
    try {
      const res = await fetch(`${API_BASE}/api/admin/seo/${endpoint}?language=${batchLang}&limit=500`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      const msg = data.message || data.status || `${endpoint} started`;
      setBatchStatus(msg + ' - AI generation takes ~40s per page, runs in background');
      // Refresh data periodically to show progress
      const interval = setInterval(() => fetchData(), 10000);
      setTimeout(() => { clearInterval(interval); setBatchStatus(null); fetchData(); }, 120000);
    } catch (e) { setBatchStatus('Error - check backend logs'); }
  };

  const handleRegenerate = async (pageType, pageKey) => {
    setRegenerating(`${pageType}:${pageKey}`);
    try {
      await fetch(`${API_BASE}/api/admin/seo/regenerate?page_type=${pageType}&page_key=${pageKey}&language=en`, {
        method: 'POST', headers,
      });
      await fetchData();
    } catch (e) { console.error(e); }
    setRegenerating(null);
  };

  const handleCacheClear = async () => {
    if (!window.confirm('Clear all AI content cache?')) return;
    await fetch(`${API_BASE}/api/admin/seo/cache-clear`, { method: 'POST', headers });
    fetchData();
  };

  if (loading) return <div className="p-6 text-slate-400">{t.loading}</div>;

  const ov = overview?.overview || {};
  const quality = overview?.quality || {};

  return (
    <div className="space-y-6" data-testid="admin-seo-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100" style={{ fontFamily: 'var(--font-heading)' }}>
          <Globe className="w-6 h-6 inline mr-2 text-emerald-400" /> {t.seoDashboard}
        </h1>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleCacheClear} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="clear-cache-btn">
            <Trash2 className="w-4 h-4 mr-1" /> {t.clearCache}
          </Button>
          <Button size="sm" onClick={fetchData} className="bg-slate-800 text-slate-300 hover:bg-slate-700">
            <RefreshCw className="w-4 h-4 mr-1" /> {t.refresh}
          </Button>
        </div>
      </div>

      {/* AI Status */}
      <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${ov.ai_enabled ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
        <Cpu className={`w-4 h-4 ${ov.ai_enabled ? 'text-emerald-400' : 'text-red-400'}`} />
        <span className="text-sm text-slate-300">
          {t.aiEngine}: <strong className={ov.ai_enabled ? 'text-emerald-400' : 'text-red-400'}>{ov.ai_enabled ? t.active : t.inactive}</strong>
        </span>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard label={t.totalContent} value={ov.total_content || 0} icon={FileText} color="emerald" />
        <StatCard label={t.match} value={ov.by_type?.match || 0} icon={BarChart3} color="blue" />
        <StatCard label={t.league} value={ov.by_type?.league || 0} icon={Globe} color="cyan" />
        <StatCard label={t.failures} value={ov.generation_failures || 0} icon={AlertTriangle} color="amber" />
        <StatCard label={t.avgTime} value={`${ov.avg_generation_time_ms || 0}ms`} icon={Clock} color="purple" />
        <StatCard label={t.avgTokens} value={ov.avg_tokens_per_generation || 0} icon={Zap} color="rose" />
      </div>

      {/* Language Distribution */}
      {ov.by_language && Object.keys(ov.by_language).length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
            <Languages className="w-4 h-4 inline mr-1" /> {t.contentByLanguage}
          </h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(ov.by_language).map(([lang, count]) => (
              <div key={lang} className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-1.5 text-xs">
                <span className="text-blue-400 font-semibold">{lang.toUpperCase()}</span>
                <span className="text-slate-400 ml-2">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin Tools Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex items-center gap-3">
          <FileEdit className="w-5 h-5 text-emerald-400 shrink-0" />
          <div>
            <span className="text-sm text-slate-300">{t.prompts}: <strong className="text-emerald-400">{ov.prompts_count || 0}</strong></span>
            <p className="text-xs text-slate-500">{t.manageFrom} {t.promptManager}</p>
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex items-center gap-3">
          <Key className="w-5 h-5 text-amber-400 shrink-0" />
          <div>
            <span className="text-sm text-slate-300">{t.keywords}: <strong className="text-amber-400">{ov.keywords_count || 0}</strong></span>
            <p className="text-xs text-slate-500">{t.manageFrom} {t.keywordManager}</p>
          </div>
        </div>
      </div>

      {/* AI Jobs Link */}
      <div className="bg-slate-900/50 border border-emerald-500/10 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Zap className="w-5 h-5 text-amber-400" />
          <div>
            <span className="text-sm text-slate-300 font-semibold">{t.contentGeneration}</span>
            <p className="text-xs text-slate-500">Manage all AI generation jobs, pause/resume, track progress</p>
          </div>
        </div>
        <a href="/admin/ai-jobs" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-lg transition-colors" data-testid="go-to-ai-jobs">
          AI Jobs →
        </a>
      </div>

      {/* SEO Health Dashboard */}
      {enhanced && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            {t.seoGrowthHealth}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            <MiniStat label="Predictions" value={enhanced.predictions?.total || 0} color="violet" />
            <MiniStat label="Programmatic" value={enhanced.programmatic?.total || 0} color="amber" />
            <MiniStat label="FAQs" value={enhanced.faq_count || 0} color="pink" />
            <MiniStat label="Trends" value={enhanced.trend_count || 0} color="teal" />
            <MiniStat label="Insights" value={enhanced.insight_count || 0} color="indigo" />
            <MiniStat label="Expired" value={enhanced.expired_content || 0} color="rose" />
          </div>
          {enhanced.quality && (
            <div className="border border-slate-700/50 rounded-lg p-3">
              <h3 className="text-xs font-semibold text-slate-400 uppercase mb-2">{t.indexingControl}</h3>
              <div className="flex gap-6 text-xs">
                <div><span className="text-slate-500">Scored: </span><span className="text-slate-300 font-bold">{enhanced.quality.scored || 0}</span></div>
                <div><span className="text-slate-500">Indexable: </span><span className="text-emerald-400 font-bold">{enhanced.quality.indexable || 0}</span></div>
                <div><span className="text-slate-500">Noindex: </span><span className="text-red-400 font-bold">{enhanced.quality.noindex || 0}</span></div>
              </div>
              {enhanced.quality.scored > 0 && (
                <div className="mt-2 h-2 bg-slate-800 rounded-full overflow-hidden flex">
                  <div className="bg-emerald-500 h-full" style={{ width: `${((enhanced.quality.indexable || 0) / enhanced.quality.scored * 100)}%` }} />
                  <div className="bg-red-500 h-full" style={{ width: `${((enhanced.quality.noindex || 0) / enhanced.quality.scored * 100)}%` }} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Page Quality Overview */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
          {t.pageQuality}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QualityCard title={t.match + 's'} data={quality.matches} t={t} />
          <QualityCard title={t.league + 's'} data={quality.leagues} t={t} />
          <QualityCard title="Bookmakers" data={quality.bookmakers} t={t} />
        </div>
      </div>

      {/* Generated Content with Filters */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">{t.content} ({pages.length})</h2>
          <div className="flex gap-2">
            <select value={pagesType} onChange={e => setPagesType(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-xs text-slate-300">
              <option value="">All Types</option>
              <option value="match">Match</option>
              <option value="league">League</option>
              <option value="bookmaker">Bookmaker</option>
            </select>
            <select value={pagesLang} onChange={e => setPagesLang(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-xs text-slate-300">
              {LANGS.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
          </div>
        </div>
        {pages.length > 0 ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {pages.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-2 px-3 hover:bg-slate-800/50 rounded-lg border border-slate-800/50">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className="border-slate-700 text-slate-400 shrink-0">{p.page_type}</Badge>
                  <span className="text-slate-300 truncate">{p.page_key}</span>
                  <Badge variant="outline" className="border-slate-700 text-slate-500 shrink-0">{p.language}</Badge>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-slate-500">{new Date(p.generated_at).toLocaleDateString()}</span>
                  <Button size="sm" variant="ghost"
                    className="text-slate-400 hover:text-emerald-400 h-6 px-2"
                    disabled={regenerating === `${p.page_type}:${p.page_key?.split(':')[1]}`}
                    onClick={() => handleRegenerate(p.page_type, p.page_key?.split(':')[1] || '')}
                    data-testid={`regenerate-${i}`}>
                    <RefreshCw className={`w-3 h-3 ${regenerating === `${p.page_type}:${p.page_key?.split(':')[1]}` ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">No content found</p>
        )}
      </div>

      {/* Logs */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">{t.generationLogs}</h2>
        {logs.length > 0 ? (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {logs.map((log, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1.5 px-2 hover:bg-slate-800/50 rounded">
                <div className="flex items-center gap-2">
                  {log.success ? <CheckCircle className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
                  <span className="text-slate-400">{log.page_type}</span>
                  <span className="text-slate-300 truncate max-w-[200px]">{log.page_key}</span>
                  <Badge variant="outline" className="border-slate-700 text-slate-500">{log.language}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  {log.tokens_used > 0 && <span className="text-slate-600">{log.tokens_used}t</span>}
                  {log.generation_time_ms > 0 && <span className="text-slate-600">{log.generation_time_ms}ms</span>}
                  {log.error && <span className="text-red-400 text-[10px]">{log.error}</span>}
                  <span className="text-slate-500">{new Date(log.timestamp).toLocaleTimeString()}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">No logs yet</p>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  const colors = {
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    purple: 'text-violet-400 bg-violet-500/10 border-violet-500/20',
    rose: 'text-rose-400 bg-rose-500/10 border-rose-500/20',
  };
  return (
    <div className={`border rounded-xl p-3 ${colors[color]}`} data-testid={`seo-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-wider opacity-80">{label}</span>
      </div>
      <p className="text-xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

function QualityCard({ title, data, t = {} }) {
  if (!data) return null;
  return (
    <div className="border border-slate-700/50 rounded-lg p-3">
      <h3 className="text-xs font-semibold text-slate-400 uppercase mb-2">{title}</h3>
      <div className="space-y-1 text-xs">
        <div className="flex justify-between"><span className="text-slate-500">{t.total || 'Total'}</span><span className="text-slate-300">{data.total}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">{t.withAi || 'With AI'}</span><span className="text-emerald-400">{data.with_ai_content}</span></div>
        {data.with_odds !== undefined && (
          <div className="flex justify-between"><span className="text-slate-500">{t.withOdds || 'With Odds'}</span><span className="text-blue-400">{data.with_odds}</span></div>
        )}
        {data.thin_pages !== undefined && (
          <div className="flex justify-between"><span className="text-slate-500">{t.thin || 'Thin'}</span><span className="text-amber-400">{data.thin_pages}</span></div>
        )}
      </div>
    </div>
  );
}


function MiniStat({ label, value, color }) {
  const colors = {
    violet: 'text-violet-400 bg-violet-500/10 border-violet-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    pink: 'text-pink-400 bg-pink-500/10 border-pink-500/20',
    teal: 'text-teal-400 bg-teal-500/10 border-teal-500/20',
    indigo: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
    rose: 'text-rose-400 bg-rose-500/10 border-rose-500/20',
  };
  return (
    <div className={`border rounded-lg p-2 text-center ${colors[color] || colors.violet}`}>
      <p className="text-lg font-bold tabular-nums">{value}</p>
      <span className="text-[9px] uppercase tracking-wider opacity-70">{label}</span>
    </div>
  );
}
